import React, { createContext, useContext, useMemo } from 'react';
import { useAdminSiteMode } from '@/entities/admin-config';
import { STALE_TIME_STATIC } from '@/shared/constants';

const SiteModeContext = createContext<any>(null);

export const useSiteMode = () => {
  const context = useContext(SiteModeContext);
  if (!context) {
    throw new Error('useSiteMode must be used within a SiteModeProvider');
  }
  return context;
};

/** Default feature flags used while the query is still loading. */
const DEFAULT_FEATURES = {
  cart_enabled: false,
  checkout_enabled: false,
  mini_cart_enabled: false,
  add_to_cart_enabled: false,
  annonces_enabled: false,
  pro_features_enabled: false,
  notify_availability_enabled: true,
  pro_registration_enabled: true,
  show_prelaunch_badge: true,
};

export const SiteModeProvider = ({ children }: any) => {
  const { data, isLoading, refetch } = useAdminSiteMode({
    staleTime: STALE_TIME_STATIC,
  });

  const mode = (data as any)?.mode ?? 'pre_launch';
  const features = (data as any)?.features ?? DEFAULT_FEATURES;
  const loading = isLoading;

  // Helper functions
  const isPreLaunch = mode === 'pre_launch';
  const isLaunched = mode === 'launched';

  const value = useMemo(
    () => ({
      mode,
      features,
      loading,
      isPreLaunch,
      isLaunched,
      canUseCart: () => features.cart_enabled,
      canUseCheckout: () => features.checkout_enabled,
      canUseAnnonces: () => features.annonces_enabled,
      canUseProFeatures: () => features.pro_features_enabled,
      shouldShowNotifyButton: () => features.notify_availability_enabled,
      shouldShowPrelaunchBadge: () => features.show_prelaunch_badge,
      refreshMode: refetch,
    }),
    [mode, features, loading, isPreLaunch, isLaunched, refetch]
  );

  return <SiteModeContext.Provider value={value}>{children}</SiteModeContext.Provider>;
};

export default SiteModeContext;
